import React, { useState, useEffect, useRef } from 'react';
import { useUserRole } from "./contexts/UserRoleContext";
import { supabase } from "./supabaseClient";
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import ZoneDialog from './ZoneDialog';
import AfvalEilandMarkerSVG from './AfvalEilandMarkerSVG';
import AfvalHalteMarkerSVG from './AfvalHalteMarkerSVG';
import HarnasMarkerSVG from './HarnasMarkerSVG';
import { AppSidebar } from './AppSidebar';
import { SidebarProvider, SidebarInset } from './components/ui/sidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function Marker({ marker, idx, addMarkerMode, setMarkers, onOptionDragStart }) {
  const streamColors = {
    pmd: '#007bff', // blue
    waste: '#222', // black
    papier: 'orange',
    glass: 'red',
    gft: 'green',
    folie: 'gold',
    colson: 'gray',
  };
  const fill = streamColors[marker.stream] || '#007bff';
  const markerRef = React.useRef(null);
  React.useEffect(() => {
    if (addMarkerMode && markerRef.current) {
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setMarkers(ms => ms.filter((_, i) => i !== idx));
        fetch(`${API_BASE}/api/markers?x=${marker.x}&y=${marker.y}&type=${encodeURIComponent(marker.type)}&stream=${encodeURIComponent(marker.stream)}`, { method: 'DELETE' });
      };
      const el = markerRef.current;
      el.addEventListener('contextmenu', handler);
      return () => {
        if (el) el.removeEventListener('contextmenu', handler);
      };
    }
  }, [addMarkerMode, idx, marker.x, marker.y, marker.type, marker.stream, setMarkers]);
  const handleContextMenu = (e) => {
    if (addMarkerMode) {
      e.preventDefault();
      e.stopPropagation();
      setMarkers(ms => ms.filter((_, i) => i !== idx));
      fetch(`${API_BASE}/api/markers?x=${marker.x}&y=${marker.y}&type=${encodeURIComponent(marker.type)}&stream=${encodeURIComponent(marker.stream)}`, { method: 'DELETE' });
    }
  };
  if (marker.type === '1100') { // already strict equality
    return (
      <rect
        ref={markerRef}
        x={marker.x - 10}
        y={marker.y - 6}
        width={20}
        height={12}
        fill={fill}
        style={addMarkerMode ? { cursor: 'pointer', pointerEvents: 'auto' } : { pointerEvents: 'none' }}
        onContextMenu={handleContextMenu}
        onMouseDown={e => {
          if (addMarkerMode && e.button === 0 && e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            if (onOptionDragStart) onOptionDragStart(idx, e);
          }
        }}

      />
    );
  } else if (marker.type === 'harnas') { // already strict equality
    return (
      <g ref={markerRef} style={addMarkerMode ? { cursor: 'pointer', pointerEvents: 'auto' } : undefined} onContextMenu={handleContextMenu} onMouseDown={e => {
        if (addMarkerMode && e.button === 0 && e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          if (onOptionDragStart) onOptionDragStart(idx, e);
        }
      }}>
        <HarnasMarkerSVG x={marker.x} y={marker.y} stroke={fill} />
      </g>
    );
  } else if (marker.type === 'afval halte') { // already strict equality
    return (
      <g ref={markerRef} style={addMarkerMode ? { cursor: 'pointer', pointerEvents: 'auto' } : undefined} onContextMenu={handleContextMenu} onMouseDown={e => {
        if (addMarkerMode && e.button === 0 && e.altKey) {
          e.preventDefault();
          e.stopPropagation();
          if (onOptionDragStart) onOptionDragStart(idx, e);
        }
      }}>
        <AfvalHalteMarkerSVG x={marker.x} y={marker.y} />
      </g>
    );
  } else if (marker.type === 'afval eiland') {
    return (
      <g ref={markerRef}
        style={addMarkerMode ? { cursor: 'pointer', pointerEvents: 'auto' } : undefined}
        onContextMenu={handleContextMenu}
        onMouseDown={e => {
          if (addMarkerMode && e.button === 0 && e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            if (onOptionDragStart) onOptionDragStart(idx, e);
          }
        }}>
        <AfvalEilandMarkerSVG x={marker.x} y={marker.y} />
      </g>
    );
  } else if (marker.type === 'peukenpiramide') {
    // Black triangle for peukenpiramide
    return (
      <polygon
        ref={markerRef}
        points={[
          [marker.x, marker.y - 5], // Top
          [marker.x - 5, marker.y + 5], // Bottom left
          [marker.x + 5, marker.y + 5]  // Bottom right
        ].map(([x, y]) => `${x},${y}`).join(' ')}
        fill="black"
        stroke="black"
        strokeWidth={1}
        style={addMarkerMode ? { cursor: 'pointer', pointerEvents: 'auto' } : { pointerEvents: 'none' }}
        onContextMenu={handleContextMenu}
        onMouseDown={e => {
          if (addMarkerMode && e.button === 0 && e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            if (onOptionDragStart) onOptionDragStart(idx, e);
          }
        }}
      />
    );
  } else if (marker.type === 'wapper') {
    // Red circle for wapper
    return (
      <circle
        ref={markerRef}
        cx={marker.x}
        cy={marker.y}
        r={6}
        fill="red"
        stroke="red"
        strokeWidth={1}
        style={addMarkerMode ? { cursor: 'pointer', pointerEvents: 'auto' } : { pointerEvents: 'none' }}
        onContextMenu={handleContextMenu}
        onMouseDown={e => {
          if (addMarkerMode && e.button === 0 && e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            if (onOptionDragStart) onOptionDragStart(idx, e);
          }
        }}
      />
    );
  } else {
    // Default: kliko or unknown
    return (
      <rect
        ref={markerRef}
        x={marker.x - 5}
        y={marker.y - 5}
        width={10}
        height={10}
        fill={fill}
        style={addMarkerMode ? { cursor: 'pointer', pointerEvents: 'auto' } : { pointerEvents: 'none' }}
        onContextMenu={handleContextMenu}
        onMouseDown={e => {
          if (addMarkerMode && e.button === 0 && e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            if (onOptionDragStart) onOptionDragStart(idx, e);
          }
        }}
      />
    );
  }
}

function isPointInPolygon(point, polygon) {
  let x = point.x, y = point.y;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    let xi = polygon[i][0], yi = polygon[i][1];
    let xj = polygon[j][0], yj = polygon[j][1];
    let intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / (yj - yi + 0.00001) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}


function MainUI() {
  // User role context
  const { role, setRole } = useUserRole();

  useEffect(() => {
    async function fetchUserRole() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: userProfile } = await supabase
          .from('Roles')
          .select('*')
          .eq('id', user.id)
          .single();
        setRole(userProfile?.role || null);
      }
    }
    fetchUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRole]);
  // Helper to add marker at double-click location
  function handleAddMarker(e) {
    if (!svgRef.current) return;
    const svgRect = svgRef.current.getBoundingClientRect();
    let offsetX = e.clientX - svgRect.left;
    let offsetY = e.clientY - svgRect.top;
    const viewBoxWidth = MAP_WIDTH / zoom;
    const viewBoxHeight = MAP_HEIGHT / zoom;
    const imageAspect = MAP_WIDTH / MAP_HEIGHT;
    const svgAspect = svgRect.width / svgRect.height;
    let x, y;
    if (svgAspect > imageAspect) {
      // Letterbox left/right
      const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
      const adjustedX = offsetX - marginX;
      x = pan.x + adjustedX * (viewBoxWidth / (svgRect.width - 2 * marginX));
      y = pan.y + offsetY * (viewBoxHeight / svgRect.height);
    } else {
      // Letterbox top/bottom
      const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
      const adjustedY = offsetY - marginY;
      x = pan.x + offsetX * (viewBoxWidth / svgRect.width);
      y = pan.y + adjustedY * (viewBoxHeight / (svgRect.height - 2 * marginY));
    }
    x = Math.max(0, Math.min(MAP_WIDTH, x));
    y = Math.max(0, Math.min(MAP_HEIGHT, y));
    // Save marker to backend with type and stream
    let markerPayload = { x, y, type: markerType, stream: markerStream };
    if (markerType === 'tafelbeugels') {
      markerPayload.tafelbeugelsAmount = tafelbeugelsAmount;
    }
    setMarkers(ms => [...ms, markerPayload]); // Optimistically add marker
    fetch(`${API_BASE}/api/markers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(markerPayload)
    });

  }

  const svgRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [newZonePoints, setNewZonePoints] = useState([]);
  const [showNewZoneDialog, setShowNewZoneDialog] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneColor, setNewZoneColor] = useState('#28a745');
  const [editZoneIdx, setEditZoneIdx] = useState(null);
  const [editZonePoints, setEditZonePoints] = useState([]);
  const [editZoneName, setEditZoneName] = useState('');
  const [editZoneColor, setEditZoneColor] = useState('#28a745');
  const [draggedPointIdx, setDraggedPointIdx] = useState(null); // index of dragged point
  const [isDraggingPoint, setIsDraggingPoint] = useState(false);
  const [mapSrc] = useState("/DV-R&C_2026-g.jpg");
  // State for Option+dragging markers
  const [draggedMarkerIdx, setDraggedMarkerIdx] = useState(null);
  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [markerDragOffset, setMarkerDragOffset] = useState({ x: 0, y: 0 }); // offset from mouse to marker on drag start
  const [originalDraggedMarker, setOriginalDraggedMarker] = useState(null); // Save original for deletion

  const MAP_WIDTH = 6592;
  const MAP_HEIGHT = 2472;
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const startPan = useRef({ x: 0, y: 0, mouseX: 0, mouseY: 0 });
  const [markers, setMarkers] = useState([]);
  // Marker add toolbar state
  const [markerType, setMarkerType] = useState('kliko');
  const [markerStream, setMarkerStream] = useState('pmd');
  const [tafelbeugelsAmount, setTafelbeugelsAmount] = useState(1);
  const [zones, setZones] = useState([]);
  const [viewMode, setViewMode] = useState(true);
  const [addMarkerMode, setAddMarkerMode] = useState(false);
  const [drawZoneMode, setDrawZoneMode] = useState(false);
  const [selectedZoneIdx, setSelectedZoneIdx] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [showZones, setShowZones] = useState(true);

  // NEW: Effect to handle "afval eiland" and "afval halte" logic
  useEffect(() => {
    if (markerType === 'wapper') {
      setMarkerStream(null);
    } else if (markerType === 'peukenpiramide') {
      setMarkerStream('peuken');
    } else if (markerType === 'afval eiland') { // already strict equality
      setMarkerStream('PMD, waste');
    } else if (markerType === 'afval halte') { // already strict equality
      setMarkerStream('waste');
    } else if (markerType === 'tafelbeugels') {
      setMarkerStream('waste');
    } else {
      setMarkerStream('pmd');
    }
  }, [markerType]);

  // Calculate dynamic minZoom
  const minZoom = Math.max(
    containerSize.width / MAP_WIDTH,
    containerSize.height / MAP_HEIGHT
  );
  const maxZoom = 5;

  // Clamp zoom and center map at min zoom
  useEffect(() => {
    if (zoom < minZoom) {
      setZoom(minZoom);
    }
    // Center map at min zoom
    if (zoom === minZoom) { // already strict equality
      setPan({
        x: (MAP_WIDTH - containerSize.width / minZoom) / 2,
        y: (MAP_HEIGHT - containerSize.height / minZoom) / 2
      });
    }
  }, [containerSize, zoom, minZoom]);

  // Track window resize
  useEffect(() => {
    function handleResize() {
      setContainerSize({ width: window.innerWidth, height: window.innerHeight });
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  //   Debug: Print state on every render
  //   console.log('[STATE] drawZoneMode:', drawZoneMode, 'newZonePoints:', newZonePoints, 'editZoneIdx:', editZoneIdx, 'showNewZoneDialog:', showNewZoneDialog);

  // Helper to zoom around center
  function zoomAroundCenter(newZoom) {
    if (!svgRef.current) {
      setZoom(newZoom);
      return;
    }
    const svgRect = svgRef.current.getBoundingClientRect();
    const centerX = svgRect.width / 2;
    const centerY = svgRect.height / 2;
    const mapCenterX = pan.x + centerX * (MAP_WIDTH / zoom) / svgRect.width;
    const mapCenterY = pan.y + centerY * (MAP_HEIGHT / zoom) / svgRect.height;
    // Clamp pan so map stays in view after zoom
    let newPanX = mapCenterX - (centerX * (MAP_WIDTH / newZoom) / svgRect.width);
    let newPanY = mapCenterY - (centerY * (MAP_HEIGHT / newZoom) / svgRect.height);
    const viewportW = MAP_WIDTH / newZoom;
    const viewportH = MAP_HEIGHT / newZoom;
    let minPanX = 0;
    let maxPanX = MAP_WIDTH - viewportW;
    let minPanY = 0;
    let maxPanY = MAP_HEIGHT - viewportH;
    if (viewportW > MAP_WIDTH) {
      minPanX = maxPanX = (MAP_WIDTH - viewportW) / 2;
    }
    if (viewportH > MAP_HEIGHT) {
      minPanY = maxPanY = (MAP_HEIGHT - viewportH) / 2;
    }
    newPanX = Math.max(minPanX, Math.min(maxPanX, newPanX));
    newPanY = Math.max(minPanY, Math.min(maxPanY, newPanY));
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  }

  // Custom double-click detector for cross-browser reliability
  useEffect(() => {
    if (drawZoneMode && newZonePoints.length > 0 && editZoneIdx === null) { // already strict equality

    }
  }, [drawZoneMode, newZonePoints, editZoneIdx]);


  function handleZoneEdit(idx) {
    setEditZoneIdx(idx);
    setEditZonePoints([...zones[idx].points]);
    setEditZoneName(zones[idx].name || '');
    setEditZoneColor(zones[idx].color || '#28a745');
    setNewZonePoints([]);
  }


  useEffect(() => {
    // if (showNewZoneDialog) {
    //   console.log('Rendering dialog');
    // }
    // const handleGlobalClick = e => {
    //   console.log('Global click event:', e.target);
    // };

  }, [showNewZoneDialog]);

  useEffect(() => {
    fetch(`${API_BASE}/api/zones`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setZones(data);
      })
      .catch(() => { });
    fetch(`${API_BASE}/api/markers`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setMarkers(data);
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!viewMode) {
      setSelectedZoneIdx(null);
      setTooltip(null);
    }
  }, [viewMode]);

  return (
    <SidebarProvider open={addMarkerMode} onOpenChange={setAddMarkerMode}>
      <div className="App flex h-screen w-screen overflow-hidden">
        <AppSidebar
          markerType={markerType}
          setMarkerType={setMarkerType}
          markerStream={markerStream}
          setMarkerStream={setMarkerStream}
          tafelbeugelsAmount={tafelbeugelsAmount}
          setTafelbeugelsAmount={setTafelbeugelsAmount}
          setAddMarkerMode={setAddMarkerMode}
          setViewMode={setViewMode}
        />
        <SidebarInset className="flex-1 relative overflow-hidden bg-[#202020]">
          {/* --- Grey overlay that disables the top bar when in add marker mode --- */}
          {addMarkerMode && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: 50,
              zIndex: 100001,
              background: 'rgba(80,80,80,0.55)',
              pointerEvents: 'all',
              transition: 'opacity 0.2s',
            }} aria-hidden="true" />
          )}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: 50,
            zIndex: 100000,
            background: '#121212',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            opacity: addMarkerMode ? 0.6 : 1,
            filter: addMarkerMode ? 'grayscale(0.4) blur(0.6px)' : undefined,
            pointerEvents: addMarkerMode ? 'none' : 'auto',
            transition: 'opacity 0.2s, filter 0.2s',
          }}>
            <h2 style={{ margin: 0, cursor: 'pointer', fontWeight: 800 }} onClick={() => {
              setViewMode(true);
              setAddMarkerMode(false);
              setDrawZoneMode(false);
              setSelectedZoneIdx(null);

              setTooltip(null);
            }}>Dreamville 2026</h2>
            <div style={{ paddingBottom: "2px" }}>
              <button
                onClick={() => zoomAroundCenter(Math.max(minZoom, Math.round((zoom - 0.1) * 100) / 100))}
                style={{ marginRight: 8, borderRadius: 2, background: '#222121', padding: "0px 7px 3px 7px", lineHeight: "18px", cursor: zoom > minZoom ? 'pointer' : 'not-allowed' }}
                disabled={zoom <= minZoom}
              >-</button>
              <span style={{ marginRight: 8, fontWeight: 500, lineHeight: "30px" }}>{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => zoomAroundCenter(Math.min(maxZoom, Math.round((zoom + 0.1) * 100) / 100))}
                style={{ borderRadius: 2, padding: "0px 7px 3px 7px", lineHeight: "18px", background: '#222121', cursor: zoom < maxZoom ? 'pointer' : 'not-allowed' }}
                disabled={zoom >= maxZoom}
              >+</button>
            </div>
            <span style={{ color: '#888' }}>(Hold ⌘ and scroll to zoom, drag to pan)</span>
            <div className='test' style={{ marginLeft: 'auto', display: 'flex', gap: 20 }}>
              {viewMode && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showZones}
                      onChange={() => setShowZones(z => !z)}
                      color="primary"
                    />
                  }
                  label="Show zones"
                  style={{ marginLeft: 10 }}
                />
              )}
              {role !== 'read' && (
                <>
                  <button onClick={() => {
                    setAddMarkerMode(true);
                    setViewMode(false);
                    setSelectedZoneIdx(null);
                    setTooltip(null);
                  }} disabled={!mapSrc || addMarkerMode || drawZoneMode} style={{ marginLeft: 10, background: '#fff', color: '#000', padding: '1px 5px' }}>
                    {addMarkerMode ? 'Double click on map to place marker' : 'Add Marker'}
                  </button>
                  <button onClick={() => {
                    setDrawZoneMode(true);
                    setViewMode(false);
                    setSelectedZoneIdx(null);
                    setTooltip(null);
                  }} disabled={!mapSrc || drawZoneMode || addMarkerMode} style={{ background: '#fff', color: '#000', padding: '1px 5px' }}>
                    Draw Zone
                  </button>
                </>
              )}
            </div>
          </div>
          <div style={{ background: "#202020" }}>
            <div
              ref={mapContainerRef}
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100vh',
                overflow: 'hidden',
              }}
            >
              {/* Marker summary box in top-left, outside SVG */}
              {viewMode && (
                <div style={{ position: 'fixed', bottom: 0, left: 0, background: '#484848', padding: "20px", color: '#fff', lineHeight: '1.5', minWidth: 240, zIndex: 1000 }}>
                  {/* <div style={{ textAlign:'left', fontWeight: 'bold' }}>Markers count</div> */}
                  {(() => {
                    const typeList = [
                      'kliko',
                      '1100',
                      'afval eiland',
                      'afval halte',
                      'houten bak',
                      'harnas'
                    ];
                    let typeCounts = {};

                    if (selectedZoneIdx !== null && zones[selectedZoneIdx]) { // already strict equality
                      // Show zone totals
                      const zone = zones[selectedZoneIdx];
                      const markersInZone = markers.filter(m => isPointInPolygon(m, zone.points));
                      typeList.forEach(type => {
                        typeCounts[type] = markersInZone.filter(m => m.type === type).length; // already strict equality
                      });
                      // Exception: kliko += 2 * afval halte + 2 * afval eiland
                      typeCounts['kliko'] += 2 * (typeCounts['afval halte'] || 0) + 2 * (typeCounts['afval eiland'] || 0);

                    } else {
                      // Show global totals
                      typeList.forEach(type => {
                        typeCounts[type] = markers.filter(m => m.type === type).length; // already strict equality
                      });
                      // Exception: kliko += 2 * afval halte + 2 * afval eiland
                      typeCounts['kliko'] += 2 * (typeCounts['afval halte'] || 0) + 2 * (typeCounts['afval eiland'] || 0);

                    }
                    return (
                      <div>
                        {selectedZoneIdx !== null && zones[selectedZoneIdx] && ( // already strict equality
                          <div style={{ textAlign: "left", marginTop: 4, color: '#007bff', fontWeight: 'bold' }}>
                            Zone: {zones[selectedZoneIdx].name || 'Unnamed'}
                          </div>
                        )}
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            {typeList.map(type => (
                              <tr key={type}>
                                <td style={{ padding: '2px 8px 2px 0', textAlign: 'left', fontWeight: 500 }}>{type.charAt(0).toUpperCase() + type.slice(1)}</td>
                                <td style={{ padding: '2px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{typeCounts[type]}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {/* <div style={{ textAlign:'right', marginTop: 8, fontWeight: 'bold' }}>Total: {total}</div> */}
                      </div>
                    );
                  })()}
                </div>
              )}
              <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox={`${pan.x} ${pan.y} ${MAP_WIDTH / zoom} ${MAP_HEIGHT / zoom}`}
                style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100vh', zIndex: 0, pointerEvents: 'auto', marginTop: '25px' }}
                onWheel={e => {
                  if (!e.metaKey) return; // Only zoom if Command is held
                  e.preventDefault();
                  const svgRect = svgRef.current.getBoundingClientRect();
                  const centerX = svgRect.width / 2;
                  const centerY = svgRect.height / 2;
                  const mapCenterX = pan.x + centerX * (MAP_WIDTH / zoom) / svgRect.width;
                  const mapCenterY = pan.y + centerY * (MAP_HEIGHT / zoom) / svgRect.height;
                  let newZoom = zoom - e.deltaY * 0.001;
                  newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
                  // Clamp pan so map stays in view after zoom
                  let newPanX = mapCenterX - (centerX * (MAP_WIDTH / newZoom) / svgRect.width);
                  let newPanY = mapCenterY - (centerY * (MAP_HEIGHT / newZoom) / svgRect.height);
                  const viewportW = MAP_WIDTH / newZoom;
                  const viewportH = MAP_HEIGHT / newZoom;
                  let minPanX = 0;
                  let maxPanX = MAP_WIDTH - viewportW;
                  let minPanY = 0;
                  let maxPanY = MAP_HEIGHT - viewportH;
                  if (viewportW > MAP_WIDTH) minPanX = maxPanX = (MAP_WIDTH - viewportW) / 2;
                  if (viewportH > MAP_HEIGHT) minPanY = maxPanY = (MAP_HEIGHT - viewportH) / 2;
                  newPanX = Math.max(minPanX, Math.min(maxPanX, newPanX));
                  newPanY = Math.max(minPanY, Math.min(maxPanY, newPanY));
                  setZoom(newZoom);
                  setPan({ x: newPanX, y: newPanY });
                }}
                onContextMenu={async e => {
                  if (addMarkerMode) {
                    e.preventDefault();
                    if (!svgRef.current) return;
                    const svgRect = svgRef.current.getBoundingClientRect();
                    const offsetX = e.clientX - svgRect.left;
                    const offsetY = e.clientY - svgRect.top;
                    const viewBoxWidth = MAP_WIDTH / zoom;
                    const viewBoxHeight = MAP_HEIGHT / zoom;
                    const imageAspect = MAP_WIDTH / MAP_HEIGHT;
                    const svgAspect = svgRect.width / svgRect.height;
                    function markerToScreen(marker) {
                      let screenX, screenY;
                      if (svgAspect > imageAspect) {
                        const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                        screenX = marginX + (marker.x - pan.x) * ((svgRect.width - 2 * marginX) / viewBoxWidth);
                        screenY = (marker.y - pan.y) * (svgRect.height / viewBoxHeight);
                      } else {
                        const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                        screenX = (marker.x - pan.x) * (svgRect.width / viewBoxWidth);
                        screenY = marginY + (marker.y - pan.y) * ((svgRect.height - 2 * marginY) / viewBoxHeight);
                      }
                      return { screenX, screenY };
                    }
                    let foundIdx = -1;
                    for (let i = 0; i < markers.length; i++) {
                      const { screenX, screenY } = markerToScreen(markers[i]);
                      const dist = Math.sqrt((offsetX - screenX) ** 2 + (offsetY - screenY) ** 2);
                      if (dist <= 4) {
                        foundIdx = i;
                        break;
                      }
                    }
                    if (foundIdx !== -1) { // already strict equality
                      const marker = markers[foundIdx];
                      setMarkers(ms => ms.filter((_, i) => i !== foundIdx));
                      await fetch(`${API_BASE}/api/markers?x=${marker.x}&y=${marker.y}&type=${encodeURIComponent(marker.type)}&stream=${encodeURIComponent(marker.stream)}`, { method: 'DELETE' });
                    }
                  }
                }}
                onMouseDown={e => {
                  if (e.button === 0 && zoom > minZoom) { // already strict equality
                    isPanning.current = true;
                    const svgRect = svgRef.current.getBoundingClientRect();
                    startPan.current = {
                      x: pan.x,
                      y: pan.y,
                      mouseX: e.clientX - svgRect.left,
                      mouseY: e.clientY - svgRect.top
                    };
                  }
                }}
                onMouseMove={e => {
                  if (isDraggingPoint && draggedPointIdx !== null && drawZoneMode && editZoneIdx !== null) { // already strict equality
                    // Convert mouse coordinates to map coordinates for zone point dragging
                    const svgRect = svgRef.current.getBoundingClientRect();
                    const offsetX = e.clientX - svgRect.left;
                    const offsetY = e.clientY - svgRect.top;
                    const viewBoxWidth = MAP_WIDTH / zoom;
                    const viewBoxHeight = MAP_HEIGHT / zoom;
                    const imageAspect = MAP_WIDTH / MAP_HEIGHT;
                    const svgAspect = svgRect.width / svgRect.height;
                    let x, y;
                    if (svgAspect > imageAspect) {
                      const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                      const adjustedX = offsetX - marginX;
                      x = pan.x + adjustedX * (viewBoxWidth / (svgRect.width - 2 * marginX));
                      y = pan.y + offsetY * (viewBoxHeight / svgRect.height);
                    } else {
                      const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                      const adjustedY = offsetY - marginY;
                      x = pan.x + offsetX * (viewBoxWidth / svgRect.width);
                      y = pan.y + adjustedY * (viewBoxHeight / (svgRect.height - 2 * marginY));
                    }
                    x = Math.max(0, Math.min(MAP_WIDTH, x));
                    y = Math.max(0, Math.min(MAP_HEIGHT, y));
                    setEditZonePoints(points =>
                      points.map((pt, i) => (i === draggedPointIdx ? [x, y] : pt))
                    );
                  }
                  // Option+Drag: marker dragging
                  if (isDraggingMarker && draggedMarkerIdx !== null) {
                    if (!e.altKey) return;
                    const svgRect = svgRef.current.getBoundingClientRect();
                    const offsetX = e.clientX - svgRect.left;
                    const offsetY = e.clientY - svgRect.top;
                    const viewBoxWidth = MAP_WIDTH / zoom;
                    const viewBoxHeight = MAP_HEIGHT / zoom;
                    const imageAspect = MAP_WIDTH / MAP_HEIGHT;
                    const svgAspect = svgRect.width / svgRect.height;
                    let x, y;
                    if (svgAspect > imageAspect) {
                      const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                      const adjustedX = offsetX - marginX;
                      x = pan.x + adjustedX * (viewBoxWidth / (svgRect.width - 2 * marginX));
                      y = pan.y + offsetY * (viewBoxHeight / svgRect.height);
                    } else {
                      const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                      const adjustedY = offsetY - marginY;
                      x = pan.x + offsetX * (viewBoxWidth / svgRect.width);
                      y = pan.y + adjustedY * (viewBoxHeight / (svgRect.height - 2 * marginY));
                    }
                    // Use drag offset to position marker under mouse
                    const dragX = x - markerDragOffset.x;
                    const dragY = y - markerDragOffset.y;
                    const newMarkers = markers.map((m, i) =>
                      i === draggedMarkerIdx ? { ...m, x: Math.max(0, Math.min(MAP_WIDTH, dragX)), y: Math.max(0, Math.min(MAP_HEIGHT, dragY)) } : m
                    );
                    setMarkers(newMarkers);
                  }
                  if (isPanning.current && e.buttons === 1 && zoom > minZoom) {
                    const svgRect = svgRef.current.getBoundingClientRect();
                    const dx = (e.clientX - svgRect.left) - startPan.current.mouseX;
                    const dy = (e.clientY - svgRect.top) - startPan.current.mouseY;
                    let newPanX = startPan.current.x - dx * (MAP_WIDTH / zoom) / svgRect.width;
                    let newPanY = startPan.current.y - dy * (MAP_HEIGHT / zoom) / svgRect.height;
                    // Clamp pan so map stays in view
                    const viewportW = MAP_WIDTH / zoom;
                    const viewportH = MAP_HEIGHT / zoom;
                    let minPanX = 0;
                    let maxPanX = MAP_WIDTH - viewportW;
                    let minPanY = 0;
                    let maxPanY = MAP_HEIGHT - viewportH;
                    if (viewportW >= MAP_WIDTH) {
                      minPanX = maxPanX = (MAP_WIDTH - viewportW) / 2;
                    }
                    if (viewportH >= MAP_HEIGHT) {
                      minPanY = maxPanY = (MAP_HEIGHT - viewportH) / 2;
                    }
                    newPanX = Math.max(minPanX, Math.min(maxPanX, newPanX));
                    newPanY = Math.max(minPanY, Math.min(maxPanY, newPanY));
                    setPan({ x: newPanX, y: newPanY });
                  }
                }}
                onMouseUp={e => {
                  isPanning.current = false;
                  setIsDraggingPoint(false);
                  setDraggedPointIdx(null);
                  // --- Marker option+drag stop
                  if (isDraggingMarker && draggedMarkerIdx !== null) {
                    setIsDraggingMarker(false);
                    const movedMarker = markers[draggedMarkerIdx];
                    setDraggedMarkerIdx(null);
                    // Fire backend save for new position by delete + add (works for all backends)
                    if (originalDraggedMarker && movedMarker) {
                      // 1. DELETE the original
                      fetch(`${API_BASE}/api/markers?x=${originalDraggedMarker.x}&y=${originalDraggedMarker.y}&type=${encodeURIComponent(originalDraggedMarker.type)}&stream=${encodeURIComponent(originalDraggedMarker.stream)}`, { method: 'DELETE' })
                        .then(() => {
                          // 2. POST the new position
                          fetch(`${API_BASE}/api/markers`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(movedMarker)
                          });
                        });
                    }
                    setOriginalDraggedMarker(null);
                  }
                }}
                onClick={e => {
                  if (!drawZoneMode && viewMode) {
                    // Check if click is inside any zone
                    if (!svgRef.current) return;
                    const svgRect = svgRef.current.getBoundingClientRect();
                    let offsetX = e.clientX - svgRect.left;
                    let offsetY = e.clientY - svgRect.top;
                    const viewBoxWidth = MAP_WIDTH / zoom;
                    const viewBoxHeight = MAP_HEIGHT / zoom;
                    const imageAspect = MAP_WIDTH / MAP_HEIGHT;
                    const svgAspect = svgRect.width / svgRect.height;
                    let x, y;
                    if (svgAspect > imageAspect) {
                      const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                      const adjustedX = offsetX - marginX;
                      x = pan.x + adjustedX * (viewBoxWidth / (svgRect.width - 2 * marginX));
                      y = pan.y + offsetY * (viewBoxHeight / svgRect.height);
                    } else {
                      const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                      const adjustedY = offsetY - marginY;
                      x = pan.x + offsetX * (viewBoxWidth / svgRect.width);
                      y = pan.y + adjustedY * (viewBoxHeight / (svgRect.height - 2 * marginY));
                    }
                    // Check if point is inside any zone
                    let foundZone = null;
                    for (let i = 0; i < zones.length; i++) {
                      if (isPointInPolygon({ x, y }, zones[i].points)) {
                        foundZone = i;
                        break;
                      }
                    }
                    if (foundZone === null) { // already strict equality
                      setSelectedZoneIdx(null); // Show global totals
                    }
                  }
                  // Existing draw zone logic
                  if (!drawZoneMode || showNewZoneDialog || editZoneIdx !== null) return; // already strict equality
                  if (editZoneIdx !== null) return; // already strict equality
                  if (!svgRef.current) return;
                  const svgRect = svgRef.current.getBoundingClientRect();
                  let offsetX = e.clientX - svgRect.left;
                  let offsetY = e.clientY - svgRect.top;
                  const viewBoxWidth = MAP_WIDTH / zoom;
                  const viewBoxHeight = MAP_HEIGHT / zoom;
                  const imageAspect = MAP_WIDTH / MAP_HEIGHT;
                  const svgAspect = svgRect.width / svgRect.height;
                  let x, y;
                  if (svgAspect > imageAspect) {
                    // Letterbox left/right
                    const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                    const adjustedX = offsetX - marginX;
                    x = pan.x + adjustedX * (viewBoxWidth / (svgRect.width - 2 * marginX));
                    y = pan.y + offsetY * (viewBoxHeight / svgRect.height);
                  } else {
                    // Letterbox top/bottom
                    const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                    const adjustedY = offsetY - marginY;
                    x = pan.x + offsetX * (viewBoxWidth / svgRect.width);
                    y = pan.y + adjustedY * (viewBoxHeight / (svgRect.height - 2 * marginY));
                  }
                  x = Math.max(0, Math.min(MAP_WIDTH, x));
                  y = Math.max(0, Math.min(MAP_HEIGHT, y));
                  // Check if click is near first point to close zone
                  if (newZonePoints.length > 2) { // already strict equality
                    const [firstX, firstY] = newZonePoints[0];
                    let screenFirstX, screenFirstY;
                    if (svgAspect > imageAspect) {
                      // Letterbox left/right
                      const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                      screenFirstX = marginX + (firstX - pan.x) * ((svgRect.width - 2 * marginX) / viewBoxWidth);
                      screenFirstY = (firstY - pan.y) * (svgRect.height / viewBoxHeight);
                    } else {
                      // Letterbox top/bottom
                      const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                      screenFirstX = (firstX - pan.x) * (svgRect.width / viewBoxWidth);
                      screenFirstY = marginY + (firstY - pan.y) * ((svgRect.height - 2 * marginY) / viewBoxHeight);
                    }
                    const dist = Math.sqrt((offsetX - screenFirstX) ** 2 + (offsetY - screenFirstY) ** 2);
                    if (dist < 12) {
                      setShowNewZoneDialog(true);
                      return;
                    }
                  }
                  setNewZonePoints([...newZonePoints, [x, y]]);
                }}
                onDoubleClick={e => {
                  if (addMarkerMode) {
                    handleAddMarker(e);
                  }
                }}
              >
                <image
                  href={mapSrc}
                  x={0}
                  y={0}
                  width={MAP_WIDTH}
                  height={MAP_HEIGHT}
                  preserveAspectRatio="none"
                  style={{ pointerEvents: 'none', filter: 'saturate(20%) contrast(30%) brightness(150%)'}}
                />
                {/* Marker summary box in top-left */}

                {drawZoneMode && newZonePoints.length > 0 && editZoneIdx === null && (
                  <g>
                    <polyline
                      points={newZonePoints.map(p => p.join(',')).join(' ')}
                      fill="rgba(40,167,69,0.2)"
                      stroke="#28a745"
                      strokeWidth={2}
                    />
                    {newZonePoints.map(([x, y], idx) => (
                      <circle
                        key={idx}
                        cx={x}
                        cy={y}
                        r={idx === 0 ? 10 : 6}
                        fill={idx === 0 ? '#007bff' : '#28a745'}
                        stroke="#333"
                        strokeWidth={2}
                      />
                    ))}
                  </g>
                )}
                {showZones && zones.map((zone, idx) => (
                  <g key={idx} onDoubleClick={e => {
                    e.stopPropagation();
                    if (addMarkerMode) {
                      handleAddMarker(e);
                      return;
                    }
                    if (drawZoneMode) {
                      handleZoneEdit(idx);
                    } else if (viewMode) {
                      // Show zone totals in summary box and highlight zone
                      setSelectedZoneIdx(idx);
                      setTooltip(null); // Remove tooltip, only use summary box
                    }
                  }} style={{ cursor: drawZoneMode ? 'pointer' : 'pointer' }}>
                    <polygon
                      points={(editZoneIdx === idx ? editZonePoints : zone.points).map(p => p.join(',')).join(' ')}
                      fill={zone.color ? zone.color + '33' : 'rgba(40,167,69,0.2)'}
                      stroke={selectedZoneIdx === idx ? '#007bff' : (editZoneIdx === idx ? 'orange' : (zone.color || '#28a745'))}
                      strokeWidth={selectedZoneIdx === idx ? 6 : (editZoneIdx === idx ? 4 : 4)}
                      style={selectedZoneIdx === idx ? { filter: 'drop-shadow(0 0 12px #007bff)' } : (editZoneIdx === idx ? { filter: 'drop-shadow(0 0 6px orange)' } : {})}
                    />
                    <polygon
                      points={(editZoneIdx === idx ? editZonePoints : zone.points).map(p => p.join(',')).join(' ')}
                      fill={zone.color ? zone.color + '33' : 'rgba(40,167,69,0.2)'}
                      stroke={editZoneIdx === idx ? 'orange' : (zone.color || '#28a745')}
                      strokeWidth={editZoneIdx === idx ? 4 : 2}
                      style={editZoneIdx === idx ? { filter: 'drop-shadow(0 0 6px orange)' } : {}}
                    />
                    {/* Render clickable lines for adding new points */}
                    {drawZoneMode && editZoneIdx === idx && (
                      <>
                        {editZonePoints.map(([x1, y1], i) => {
                          if (editZonePoints.length < 2) return null;
                          const [x2, y2] = editZonePoints[(i + 1) % editZonePoints.length];
                          return (
                            <line
                              key={`seg-${i}`}
                              x1={x1}
                              y1={y1}
                              x2={x2}
                              y2={y2}
                              stroke="transparent"
                              strokeWidth={16}
                              onClick={e => {
                                e.stopPropagation();
                                // Convert mouse to map coordinates
                                const svgRect = svgRef.current.getBoundingClientRect();
                                const offsetX = e.clientX - svgRect.left;
                                const offsetY = e.clientY - svgRect.top;
                                const viewBoxWidth = MAP_WIDTH / zoom;
                                const viewBoxHeight = MAP_HEIGHT / zoom;
                                const imageAspect = MAP_WIDTH / MAP_HEIGHT;
                                const svgAspect = svgRect.width / svgRect.height;
                                let x, y;
                                if (svgAspect > imageAspect) {
                                  const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                                  const adjustedX = offsetX - marginX;
                                  x = pan.x + adjustedX * (viewBoxWidth / (svgRect.width - 2 * marginX));
                                  y = pan.y + offsetY * (viewBoxHeight / svgRect.height);
                                } else {
                                  const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                                  const adjustedY = offsetY - marginY;
                                  x = pan.x + offsetX * (viewBoxWidth / svgRect.width);
                                  y = pan.y + adjustedY * (viewBoxHeight / (svgRect.height - 2 * marginY));
                                }
                                x = Math.max(0, Math.min(MAP_WIDTH, x));
                                y = Math.max(0, Math.min(MAP_HEIGHT, y));
                                // Insert new point between i and i+1
                                setEditZonePoints(points => {
                                  const newPoints = [...points];
                                  newPoints.splice(i + 1, 0, [x, y]);
                                  return newPoints;
                                });
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          );
                        })}
                        {editZonePoints.map(([x, y], pidx) => (
                          <circle
                            key={pidx}
                            cx={x}
                            cy={y}
                            r={8}
                            fill="black"
                            stroke="white"
                            strokeWidth={2}
                            onMouseDown={e => {
                              e.stopPropagation();
                              setDraggedPointIdx(pidx);
                              setIsDraggingPoint(true);
                            }}
                            style={{ cursor: 'grab' }}
                          />
                        ))}
                      </>
                    )}
                    {zone.name && (
                      <text
                        x={zone.points.reduce((sum, p) => sum + p[0], 0) / zone.points.length}
                        y={zone.points.reduce((sum, p) => sum + p[1], 0) / zone.points.length}
                        textAnchor="middle"
                        fill={zone.color || '#28a745'}
                        fontWeight="800"
                        fontSize="22"
                        text-shadow="2px 2px #ff0000"
                      >
                        {zone.name}
                      </text>
                    )}
                  </g>
                ))}

                {/* Marker color mapping by stream */}
                {markers.map((marker, idx) => (
                  <g key={idx}>
                    <Marker
                      marker={marker}
                      idx={idx}
                      addMarkerMode={addMarkerMode}
                      setMarkers={setMarkers}
                      onOptionDragStart={(dragIdx, evt) => {
                        // convert mouse position to svg map coordinates
                        if (!svgRef.current) return;
                        const svgRect = svgRef.current.getBoundingClientRect();
                        const offsetX = evt.clientX - svgRect.left;
                        const offsetY = evt.clientY - svgRect.top;
                        const viewBoxWidth = MAP_WIDTH / zoom;
                        const viewBoxHeight = MAP_HEIGHT / zoom;
                        const imageAspect = MAP_WIDTH / MAP_HEIGHT;
                        const svgAspect = svgRect.width / svgRect.height;
                        let x, y;
                        if (svgAspect > imageAspect) {
                          const marginX = (svgRect.width - svgRect.height * imageAspect) / 2;
                          const adjustedX = offsetX - marginX;
                          x = pan.x + adjustedX * (viewBoxWidth / (svgRect.width - 2 * marginX));
                          y = pan.y + offsetY * (viewBoxHeight / svgRect.height);
                        } else {
                          const marginY = (svgRect.height - svgRect.width / imageAspect) / 2;
                          const adjustedY = offsetY - marginY;
                          x = pan.x + offsetX * (viewBoxWidth / svgRect.width);
                          y = pan.y + adjustedY * (viewBoxHeight / (svgRect.height - 2 * marginY));
                        }
                        // Compute offset from marker center
                        const markerX = markers[dragIdx].x;
                        const markerY = markers[dragIdx].y;
                        setDraggedMarkerIdx(dragIdx);
                        setIsDraggingMarker(true);
                        setMarkerDragOffset({ x: x - markerX, y: y - markerY });
                        setOriginalDraggedMarker({ ...markers[dragIdx] });
                      }}
                    />
                    {marker.type === 'tafelbeugels' && marker.tafelbeugelsAmount > 0 && (
                      <text
                        x={marker.x}
                        y={marker.y - 12}
                        textAnchor="middle"
                        fill="#007bff"
                        fontWeight="bold"
                        fontSize="19"
                        stroke="#fff"
                        strokeWidth="0.5"
                        style={{ paintOrder: 'stroke', userSelect: 'none', pointerEvents: 'none' }}
                      >
                        {marker.tafelbeugelsAmount + ' tafelbeugels'}
                      </text>
                    )}
                  </g>
                ))}



                {tooltip && (
                  <text
                    x={tooltip.x}
                    y={tooltip.y}
                    textAnchor="middle"
                    fill="#333"
                    fontWeight="bold"
                    style={{ pointerEvents: 'none', userSelect: 'none', background: 'rgba(255,255,255,0.8)' }}
                  >
                    {tooltip.text}
                  </text>
                )}

              </svg>
              {showNewZoneDialog && (
                <ZoneDialog
                  show={true}
                  initialName={newZoneName}
                  initialColor={newZoneColor}
                  onSave={async (name, color) => {
                    const newZone = { points: newZonePoints, name, color };
                    const res = await fetch(`${API_BASE}/api/zones`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(newZone)
                    });
                    const result = await res.json();
                    if (result.uid) newZone.uid = result.uid;
                    setZones(zs => [...zs, newZone]);
                    setNewZonePoints([]);
                    setNewZoneName('');
                    setNewZoneColor('#28a745');
                    setShowNewZoneDialog(false);
                    setDrawZoneMode(false);
                    setViewMode(true);
                  }}
                  onCancel={() => {
                    setShowNewZoneDialog(false);
                    setNewZonePoints([]);
                    setNewZoneName('');
                    setNewZoneColor('#28a745');
                  }}
                />
              )}
              {drawZoneMode && editZoneIdx !== null && zones[editZoneIdx] && zones[editZoneIdx].points && (
                <g>
                  {zones[editZoneIdx].points.map(([x, y], idx) => (
                    <circle
                      key={idx}
                      cx={x}
                      cy={y}
                      r={8}
                      fill="black"
                      stroke="white"
                      strokeWidth={2}
                    />
                  ))}
                </g>
              )}
              {drawZoneMode && editZoneIdx !== null && (
                <ZoneDialog
                  show={true}
                  initialName={editZoneName}
                  initialColor={editZoneColor}
                  onSave={async (name, color) => {
                    const updatedZone = { ...zones[editZoneIdx], points: editZonePoints, name, color };
                    if (!updatedZone.uid) {
                      // fallback: assign a uid if missing (shouldn't happen)
                      updatedZone.uid = Date.now().toString() + Math.floor(Math.random() * 10000).toString();
                    }
                    await fetch(`${API_BASE}/api/zones`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(updatedZone)
                    });
                    setZones(zs => zs.map((z, i) => i === editZoneIdx ? updatedZone : z));
                    setEditZoneIdx(null);
                    setEditZonePoints([]);
                    setEditZoneName('');
                    setEditZoneColor('#28a745');
                    setDrawZoneMode(false);
                    setViewMode(true);
                  }}
                  onCancel={() => {
                    setEditZoneIdx(null);
                    setEditZonePoints([]);
                    setEditZoneName('');
                    setEditZoneColor('#28a745');
                    // Optionally keep drawZoneMode true if you want to continue drawing after cancel
                  }
                  }
                  onDelete={async () => {
                    const zoneToDelete = zones[editZoneIdx];
                    if (!zoneToDelete || !zoneToDelete.uid) return;
                    await fetch(`${API_BASE}/api/zones?uid=${zoneToDelete.uid}`, { method: 'DELETE' });
                    setZones(zs => zs.filter((_, i) => i !== editZoneIdx));
                    setEditZoneIdx(null);
                    setEditZonePoints([]);
                    setEditZoneName('');
                    setEditZoneColor('#28a745');
                    setDrawZoneMode(false);
                    setViewMode(true);
                  }}
                />
              )})
            </div>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}

export default MainUI;
